import BaseSync from "./base";
import { CancelablePromise } from "../utils/cancelablePromise";
import { COACHING_SESSION_STATUS, COACHING_SYNC_LOG_PREFIX, NEW_SESSION_KEY } from "../modules/coaching/constants";
import { CoachingActions } from "../modules/coaching/store/actions";
import { CoachingCommentSync } from "./coachingCommentSync";
import { CoachingCompletionSync } from "./coachingCompletionSync";
import { CoachingNewSessionSync } from "./coachingNewSessionSync";
import { CoachingNotificationService } from "../modules/coaching/services/coachingNotificationService";
import { CoachingSessionService } from "../modules/coaching/services/coachingSessionService";
import { CoachingSessionStatus, ICoachingSession, IRawCoachingSession } from "../modules/coaching/store/types";
import { IApiError } from "../utils/api.interfaces";
import { MeasureName } from "../utils/performanceProfiler/measureName";
import PerformanceProfileBatcher from "../utils/performanceProfiler/performanceProfileBatcher";
import { isApiError, isTerminalError } from "./coachingSyncErrors";
import { isPendingComment, isPendingCompletion } from "./coachingSyncFilters";
import * as logger from "../utils/logger";

const SYNC_LABEL = "Coaching";

/** Stage tag appended to the shared root so the whole flow greps by `[CoachingSync]`. */
const LOG_PREFIX = `${ COACHING_SYNC_LOG_PREFIX } [Orchestrator]`;

type TSyncResult = [string, IApiError?];

/**
 * Orchestrates offline coaching sync. Each cycle it:
 *   1. runs the queued NEW-session resolution (if any): reuse/create the real session, re-key the
 *      "new" buckets onto it, or drop the work if it can't be resolved (bounded retry budget)
 *   2. for every real-id session key with pending work, if the server session is already finalized
 *      it drops pending comments (with a notification) and settles a queued completion silently
 *      (already satisfied); otherwise delegates comments to CoachingCommentSync and completion to
 *      CoachingCompletionSync
 * The "new" sentinel is handled only by step 1; the push paths in step 2 only ever see real ids.
 * The collaborators own their own error/retry policy; this class owns ordering and session state.
 */
export default class CoachingSync extends BaseSync {
    private readonly notifications = new CoachingNotificationService();
    private readonly sessionService = new CoachingSessionService(this.api);
    private readonly commentSync = new CoachingCommentSync(
        this.api,
        this.store,
        this.sessionService,
        this.notifications
    );
    private readonly completionSync = new CoachingCompletionSync(
        this.api,
        this.store,
        this.sessionService,
        this.notifications
    );
    private readonly resolutionSync = new CoachingNewSessionSync(
        this.store,
        this.sessionService,
        this.notifications,
        this.commentSync,
        this.completionSync
    );

    synchronize(profilingBatcher?: PerformanceProfileBatcher) {
        return this.createProfiledSync<TSyncResult>(
            MeasureName.SyncCoaching,
            () => new CancelablePromise<TSyncResult>((resolve) => {
                this.flush()
                    .then(() => resolve([SYNC_LABEL]))
                    .catch((error) => resolve([SYNC_LABEL, error as IApiError]));
            }),
            profilingBatcher
        );
    }

    private async flush(): Promise<void> {
        const userId = this.api.getUser()?.id;
        if (!userId) {
            logger.log(
                logger.Source.DEBUG,
                `${ LOG_PREFIX } no active user, skipping`,
                this.api.getUser()
            );
            return;
        }

        logger.log(
            logger.Source.DEBUG,
            `${ LOG_PREFIX } flush cycle started for user ${ userId }`,
            this.api.getUser()
        );

        // Resolve the NEW session first: this re-keys "new" → realId so its work is picked up below
        // this same cycle, or drops it when it can't be resolved within the retry budget.
        if (this.store.getState().coaching?.[userId]?.newSessionResolution) {
            logger.log(
                logger.Source.DEBUG,
                `${ LOG_PREFIX } newSessionResolution op exists, running resolution`,
                this.api.getUser()
            );
            await this.resolutionSync.resolve(userId);
        }

        // Re-read after resolution (the "new" buckets may have just been re-keyed).
        const state = this.store.getState().coaching?.[userId];
        const comments = state?.comments ?? {};
        const completions = state?.pendingCompletions ?? {};

        // The "new" key is owned by resolveNewSession; the push paths only handle real ids.
        const keysWithPendingWork = new Set<string>();
        Object.keys(comments).forEach((key) => {
            if (key !== NEW_SESSION_KEY && (comments[key] ?? []).some(isPendingComment)) {
                keysWithPendingWork.add(key);
            }
        });
        Object.keys(completions).forEach((key) => {
            const record = completions[key];
            if (key !== NEW_SESSION_KEY && record && isPendingCompletion(record)) {
                keysWithPendingWork.add(key);
            }
        });

        const workCount = keysWithPendingWork.size;
        const workKeys = Array.from(keysWithPendingWork);
        if (workCount === 0) {
            logger.log(
                logger.Source.DEBUG,
                `${ LOG_PREFIX } nothing to flush`,
                this.api.getUser()
            );
            return;
        }

        logger.log(
            logger.Source.DEBUG,
            `${ LOG_PREFIX } ${ workCount } session(s) with pending work: ${ workKeys.join(", ") }`,
            this.api.getUser()
        );

        for (const key of workKeys) {
            await this.flushSession(userId, key);
        }
    }

    private async flushSession(userId: string, sessionId: string): Promise<void> {
        const state = this.store.getState().coaching?.[userId];
        const capturedComments = (state?.comments?.[sessionId] ?? [])
            .filter(isPendingComment)
            .slice()
            .sort((a, b) => (a.dateTime < b.dateTime ? -1 : a.dateTime > b.dateTime ? 1 : 0));
        const rawCompletion = state?.pendingCompletions?.[sessionId];
        const pendingCompletion = rawCompletion && isPendingCompletion(rawCompletion) ? rawCompletion : undefined;

        const commentCount = capturedComments.length;
        const hasCompletion = !!pendingCompletion;
        logger.log(
            logger.Source.DEBUG,
            `${ LOG_PREFIX } flushSession ${ sessionId }: ${ commentCount } pending comment(s), completion pending: ${ hasCompletion }`,
            this.api.getUser()
        );

        if (!capturedComments.length && !pendingCompletion) {
            logger.log(
                logger.Source.DEBUG,
                `${ LOG_PREFIX } session ${ sessionId } has nothing pending, skipping`,
                this.api.getUser()
            );
            return;
        }

        try {
            const session = await this.sessionService.getSession(sessionId);
            const sessionState = session?.state;
            logger.log(
                logger.Source.DEBUG,
                `${ LOG_PREFIX } fetched session ${ sessionId } state: ${ sessionState }`,
                this.api.getUser()
            );

            if (session?.state === COACHING_SESSION_STATUS.COMPLETED) {
                logger.log(
                    logger.Source.DEBUG,
                    `${ LOG_PREFIX } session ${ sessionId } is COMPLETED — dropping pending comments, settling any queued completion`,
                    this.api.getUser()
                );
                this.commentSync.drop(userId, sessionId, capturedComments, "sessionFinalized");
                if (capturedComments.length > 0) {
                    logger.log(
                        logger.Source.DEBUG,
                        `${ LOG_PREFIX } firing notifyCommentDroppedDueToCompletion for session ${ sessionId }`,
                        this.api.getUser()
                    );
                    this.notifications.notifyCommentDroppedDueToCompletion(userId, sessionId);
                }

                if (pendingCompletion) {
                    logger.log(
                        logger.Source.DEBUG,
                        `${ LOG_PREFIX } queued completion for session ${ sessionId } already satisfied on server — settling silently`,
                        this.api.getUser()
                    );
                    this.store.dispatch(
                        CoachingActions.removePendingSessionCompletion({ userId, sessionId })
                    );
                }
                // The just-fetched raw shows the finalized state; patch the single stored session so
                // the main list reflects it.
                this.patchStoredSession(userId, sessionId, session);
                return;
            }

            const outcome = await this.commentSync.push(userId, sessionId, capturedComments);
            logger.log(
                logger.Source.DEBUG,
                `${ LOG_PREFIX } comment push outcome for session ${ sessionId }: ${ outcome }`,
                this.api.getUser()
            );

            if (outcome === "pending") {
                logger.log(
                    logger.Source.DEBUG,
                    `${ LOG_PREFIX } comment(s) still pending for session ${ sessionId }, deferring completion to next cycle`,
                    this.api.getUser()
                );
                // A comment is awaiting a retry — leave the completion for the next cycle so it
                // never lands before its comments.
                return;
            }

            if (pendingCompletion) {
                logger.log(
                    logger.Source.DEBUG,
                    `${ LOG_PREFIX } about to push completion for session ${ sessionId }`,
                    this.api.getUser()
                );
                await this.completionSync.push(userId, sessionId, pendingCompletion);

                const settled = this.store.getState().coaching?.[userId]?.pendingCompletions?.[sessionId];
                if (settled?.syncStatus === "synced") {
                    this.markStoredSessionCompleted(userId, sessionId, pendingCompletion.requestedDateTime);
                    this.store.dispatch(CoachingActions.removePendingSessionCompletion({ userId, sessionId }));
                }
            }
        } catch (error) {
            // The push paths settle their own API errors; only the getSession state-check can throw
            // one here. A non-API fault (incl. one a collaborator rethrew) surfaces; a terminal
            // error fails the work; a transient one leaves it pending to retry next cycle.
            if (!isApiError(error)) {
                const errorMessage = error instanceof Error ? error.message : String(error);
                logger.log(
                    logger.Source.DEBUG,
                    `${ LOG_PREFIX } non-API error in session ${ sessionId }: ${ errorMessage }, rethrowing`,
                    this.api.getUser()
                );
                throw error;
            }
            const apiError = error;
            if (isTerminalError(error)) {
                logger.log(
                    logger.Source.DEBUG,
                    `${ LOG_PREFIX } terminal error for session ${ sessionId }: ${ apiError.message }, dropping all work`,
                    this.api.getUser()
                );
                this.commentSync.drop(userId, sessionId, capturedComments, "syncFailed");
                if (pendingCompletion) {
                    this.completionSync.drop(userId, sessionId, "syncFailed");
                }
            } else {
                logger.log(
                    logger.Source.DEBUG,
                    `${ LOG_PREFIX } transient error for session ${ sessionId }: ${ apiError.message }, leaving pending for retry`,
                    this.api.getUser()
                );
            }
        }
    }

    /**
     * Patch the single stored session with the state fields from a freshly-fetched raw session so
     * the main coaching list reflects the new server state without a full refetch. Overlays only the
     * state fields onto the existing session, preserving its mapped `eventSummaries`. No-op when the
     * session isn't in the store (updateCoachingSession is itself a no-op on an unknown id).
     */
    private patchStoredSession(userId: string, sessionId: string, raw: IRawCoachingSession): void {
        const existing = this.store
            .getState()
            .coaching?.[userId]?.sessions.find((s) => s.id === sessionId);
        if (!existing) {
            return;
        }
        this.store.dispatch(
            CoachingActions.updateCoachingSession({
                userId,
                session: {
                    ...(existing as ICoachingSession),
                    status: raw.state as CoachingSessionStatus,
                    acknowledgementDateTime: raw.acknowledgementDateTime,
                    modifiedDate: raw.modifiedDateTime
                }
            })
        );
    }

    /**
     * Optimistically flip the stored session to Completed after its offline completion synced, so the
     * main coaching list reflects it without an API call. Preserves the existing `eventSummaries`.
     * No-op when the session isn't in the store (updateCoachingSession is itself a no-op on an
     * unknown id).
     */
    private markStoredSessionCompleted(userId: string, sessionId: string, acknowledgementDateTime: string): void {
        const existing = this.store
            .getState()
            .coaching?.[userId]?.sessions.find((s) => s.id === sessionId);
        if (!existing) {
            return;
        }
        this.store.dispatch(
            CoachingActions.updateCoachingSession({
                userId,
                session: {
                    ...(existing as ICoachingSession),
                    status: COACHING_SESSION_STATUS.COMPLETED as CoachingSessionStatus,
                    acknowledgementDateTime
                }
            })
        );
    }
}
